import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  Fab,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TaxRegimeSelector from "./components/TaxRegimeSelector";
import LucroRealConfig from "./components/tax-configs/LucroRealConfig";
import LucroPresumidoConfig from "./components/tax-configs/LucroPresumidoConfig";
import SimplesNacionalConfig from "./components/tax-configs/SimplesNacionalConfig";
import SpecialRegimeCard from "./components/SpecialRegimeCard";
import {
  useGetMarketplaceActualTaxProfile,
  useUpdateMarketplaceActualTaxProfileConfiguration,
  useUpdateMarketplaceActualTaxProfile,
} from "../../client/hooks/marketplace/marketplace.queries";

export default function ImpostosPage() {
  // Hook para buscar dados do backend
  const {
    data: taxData,
    isLoading,
    error,
    refetch,
  } = useGetMarketplaceActualTaxProfile();

  // Estado para controlar execução do update de configurações
  const [executeUpdate, setExecuteUpdate] = useState(false);
  const [updatePayload, setUpdatePayload] = useState(null);

  // Estado para controlar execução do update de tax_profile
  const [executeProfileUpdate, setExecuteProfileUpdate] = useState(false);
  const [profileUpdatePayload, setProfileUpdatePayload] = useState(null);

  // Hook para update dos dados de configuração
  const {
    data: updateResult,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateMarketplaceActualTaxProfileConfiguration(
    taxData?.tax?.id,
    updatePayload,
    executeUpdate
  );

  // Hook para update do tax_profile
  const {
    data: profileUpdateResult,
    isLoading: isProfileUpdating,
    error: profileUpdateError,
  } = useUpdateMarketplaceActualTaxProfile(
    profileUpdatePayload,
    executeProfileUpdate
  );

  // Estado para rastrear as alterações de regime
  const [selectedRegime, setSelectedRegime] = useState("");
  // Estado temporário para o regime (usado antes de salvar)
  const [tempSelectedRegime, setTempSelectedRegime] = useState("");

  // Estado para rastrear o regime especial
  const [specialRegimeEnabled, setSpecialRegimeEnabled] = useState(false);

  // Estado temporário para o regime especial (usado antes de salvar)
  const [tempSpecialRegimeEnabled, setTempSpecialRegimeEnabled] =
    useState(false);

  // Flag para indicar se existem alterações pendentes
  const [pendingChanges, setPendingChanges] = useState(false);

  // Flag para controlar se os dados já foram carregados inicialmente
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Efeito para carregar dados do backend sempre que chegarem
  useEffect(() => {
    if (taxData?.tax) {
      const { tax } = taxData;

      // Só atualiza se for carregamento inicial OU se não estiver fazendo update
      if (!initialDataLoaded || !executeProfileUpdate) {
        setSelectedRegime(tax.regime);
        setTempSelectedRegime(tax.regime);
        setSpecialRegimeEnabled(tax.regimeEspecialEnabled);
        setTempSpecialRegimeEnabled(tax.regimeEspecialEnabled);

        // Salva no localStorage para manter compatibilidade
        localStorage.setItem("selected_tax_regime", tax.regime);
        localStorage.setItem(
          "special_regime_enabled",
          tax.regimeEspecialEnabled.toString()
        );

        setInitialDataLoaded(true);
      }
    }
  }, [taxData, initialDataLoaded, executeProfileUpdate]);

  // Efeito para monitorar resultado do update
  useEffect(() => {
    if (updateResult && executeUpdate) {
      showAlert(
        "Configurações de impostos atualizadas com sucesso!",
        "success"
      );
      setExecuteUpdate(false);
      setUpdatePayload(null);
      setPendingChanges(false);

      // Atualiza os estados principais com os valores temporários
      setSelectedRegime(tempSelectedRegime);
      setSpecialRegimeEnabled(tempSpecialRegimeEnabled);

      // Recarrega os dados do backend em background
      refetch();
    }
  }, [
    updateResult,
    executeUpdate,
    tempSelectedRegime,
    tempSpecialRegimeEnabled,
  ]);

  // Efeito para monitorar erros do update de configurações
  useEffect(() => {
    if (updateError && executeUpdate) {
      showAlert(
        `Erro ao atualizar configurações: ${updateError.message}`,
        "error"
      );
      setExecuteUpdate(false);
      setUpdatePayload(null);
    }
  }, [updateError, executeUpdate]);

  // Efeito para monitorar resultado do update do tax_profile
  useEffect(() => {
    if (profileUpdateResult && executeProfileUpdate) {
      showAlert("Regime tributário alterado com sucesso!", "success");
      setExecuteProfileUpdate(false);
      setProfileUpdatePayload(null);

      // Atualiza os estados principais com o novo regime
      setSelectedRegime(tempSelectedRegime);

      // Recarrega os dados do backend e força atualização
      refetch();
    }
  }, [profileUpdateResult, executeProfileUpdate, tempSelectedRegime]);

  // Efeito para monitorar erros do update do tax_profile
  useEffect(() => {
    if (profileUpdateError && executeProfileUpdate) {
      showAlert(
        `Erro ao alterar regime tributário: ${profileUpdateError.message}`,
        "error"
      );
      setExecuteProfileUpdate(false);
      setProfileUpdatePayload(null);
      // Reverte o regime para o valor anterior
      setTempSelectedRegime(selectedRegime);
    }
  }, [profileUpdateError, executeProfileUpdate, selectedRegime]);

  // Verificar se há alterações pendentes quando os estados temporários mudam
  useEffect(() => {
    // Verificar se o regime selecionado mudou
    const regimeChanged = selectedRegime !== tempSelectedRegime;

    // Verificar se o estado do regime especial mudou
    const specialRegimeChanged =
      specialRegimeEnabled !== tempSpecialRegimeEnabled;

    // Atualizar o estado de alterações pendentes
    setPendingChanges(regimeChanged || specialRegimeChanged);
  }, [
    selectedRegime,
    tempSelectedRegime,
    specialRegimeEnabled,
    tempSpecialRegimeEnabled,
  ]);

  const [alert, setAlert] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");

  const showAlert = (msg, type = "info") => {
    setMessage(msg);
    setSeverity(type);
    setAlert(true);
  };

  // Função para atualizar o tax_profile quando regime é alterado
  const handleTaxProfileUpdate = (newRegime) => {
    const updateData = {
      tax_profile: newRegime,
    };

    setProfileUpdatePayload(updateData);
    setExecuteProfileUpdate(true);
  };

  const handleRegimeChange = (regime) => {
    // Se estiver mudando para Simples Nacional, desativa regime especial
    if (regime === "SIMPLES_NACIONAL" && tempSpecialRegimeEnabled) {
      setTempSpecialRegimeEnabled(false);
    }

    setTempSelectedRegime(regime);
    setPendingChanges(true);
  };

  const handleSpecialRegimeToggle = (enabled) => {
    setTempSpecialRegimeEnabled(enabled);
    setPendingChanges(true);
  };
  // Função para salvar as alterações
  const handleSaveChanges = () => {
    // Verifica se houve alteração no regime para Simples Nacional
    if (tempSelectedRegime === "SIMPLES_NACIONAL" && tempSpecialRegimeEnabled) {
      setTempSpecialRegimeEnabled(false);
      showAlert(
        "Regime especial foi desativado pois não é compatível com Simples Nacional",
        "warning"
      );
    }

    // Verifica se há ID do tax para fazer o update
    if (!taxData?.tax?.id) {
      showAlert("Erro: ID do perfil fiscal não encontrado", "error");
      return;
    }

    // Aplica as alterações localmente
    setSelectedRegime(tempSelectedRegime);
    setSpecialRegimeEnabled(tempSpecialRegimeEnabled);
    localStorage.setItem("selected_tax_regime", tempSelectedRegime);
    localStorage.setItem(
      "special_regime_enabled",
      tempSpecialRegimeEnabled.toString()
    );

    // Prepara dados para envio ao backend
    const updateData = prepareRegimeData();

    // Configura para executar o update
    setUpdatePayload(updateData);
    setExecuteUpdate(true);
  };

  // Função para preparar dados específicos do regime para envio
  const prepareRegimeData = () => {
    const baseData = {
      regimeEspecialEnabled: tempSpecialRegimeEnabled,
    };

    switch (tempSelectedRegime) {
      case "LUCRO_REAL": {
        const lucroRealConfig = localStorage.getItem(
          "tax_regime_config_lucro_real"
        );
        const config = lucroRealConfig ? JSON.parse(lucroRealConfig) : {};
        const specialConfig = localStorage.getItem(
          `special_regime_config_${tempSelectedRegime}`
        );
        const special = specialConfig ? JSON.parse(specialConfig) : {};

        return {
          ...baseData,
          regimeEspecial: {
            icms_nacional: parseFloat(special.icms_nacional) || 0,
            icms_importado: parseFloat(special.icms_importado) || 0,
          },
          pisCofinsSellerFreightEnabled:
            config.pisCofinsSomarFreteComissao || false,
          pisCofinsCommissionEnabled:
            taxData?.tax?.pisCofinsCommissionEnabled || false,
          pisCofinsCommission: taxData?.tax?.pisCofinsCommission || 1.65,
          icmsAliquota: parseFloat(
            config.icmsAliquota || taxData?.tax?.icmsAliquota || 12
          ),
          icmsAliquotaSellerFreightEnabled: config.icmsFreteChecked || false,
        };
      }

      case "SIMPLES_NACIONAL": {
        const simplesConfig = localStorage.getItem(
          "tax_regime_config_simples_nacional"
        );
        const config = simplesConfig ? JSON.parse(simplesConfig) : {};

        return {
          regimeEspecialEnabled: false, // SIMPLES_NACIONAL não tem regime especial
          icmsRegimeSimples: parseFloat(config.icmsPercentage || 3),
          pisCofinsRegimeSimples: parseFloat(config.pisCofinsPercentage || 4),
        };
      }

      case "LUCRO_PRESUMIDO": {
        const lucroPresumidoConfig = localStorage.getItem(
          "tax_regime_config_lucro_presumido"
        );
        const config = lucroPresumidoConfig
          ? JSON.parse(lucroPresumidoConfig)
          : {};
        const specialConfig = localStorage.getItem(
          `special_regime_config_${tempSelectedRegime}`
        );
        const special = specialConfig ? JSON.parse(specialConfig) : {};

        return {
          ...baseData,
          regimeEspecial: {
            icms_nacional: parseFloat(special.icms_nacional) || 0,
            icms_importado: parseFloat(special.icms_importado) || 0,
          },
          pisCofins: parseFloat(config.pisCofinsPercentage || 4),
          icmsAliquota: parseFloat(
            config.icmsAliquota || taxData?.tax?.icmsAliquota || 12
          ),
          icmsAliquotaSellerFreightEnabled: config.icmsFreteChecked || false,
        };
      }

      default:
        return baseData;
    }
  };
  const renderConfigComponent = () => {
    const taxInfo = taxData?.tax || {};

    switch (tempSelectedRegime) {
      case "LUCRO_REAL":
        return (
          <LucroRealConfig
            onConfigChange={() => setPendingChanges(true)}
            onAlert={showAlert}
            initialData={{
              icmsAliquota: taxInfo.icmsAliquota,
              icmsAliquotaSellerFreightEnabled:
                taxInfo.icmsAliquotaSellerFreightEnabled,
              pisCofinsSellerFreightEnabled:
                taxInfo.pisCofinsSellerFreightEnabled,
              pisCofinsCommissionEnabled: taxInfo.pisCofinsCommissionEnabled,
              pisCofinsCommission: taxInfo.pisCofinsCommission,
              pisCofins: taxInfo.pisCofins,
            }}
          />
        );
      case "LUCRO_PRESUMIDO":
        return (
          <LucroPresumidoConfig
            onConfigChange={() => setPendingChanges(true)}
            onAlert={showAlert}
            initialData={{
              icmsAliquota: taxInfo.icmsAliquota,
              icmsAliquotaSellerFreightEnabled:
                taxInfo.icmsAliquotaSellerFreightEnabled,
              pisCofinsSellerFreightEnabled:
                taxInfo.pisCofinsSellerFreightEnabled,
              pisCofinsCommissionEnabled: taxInfo.pisCofinsCommissionEnabled,
              pisCofinsCommission: taxInfo.pisCofinsCommission,
              pisCofins: taxInfo.pisCofins,
            }}
          />
        );
      case "SIMPLES_NACIONAL":
        return (
          <SimplesNacionalConfig
            onConfigChange={() => setPendingChanges(true)}
            onAlert={showAlert}
            initialData={{
              icmsAliquota: taxInfo.icmsAliquota,
              icmsRegimeSimples: taxInfo.icmsRegimeSimples,
              pisCofinsRegimeSimples: taxInfo.pisCofinsRegimeSimples,
            }}
          />
        );
      default:
        return null;
    }
  };

  // Exibe loading enquanto carrega dados
  if (isLoading) {
    return (
      <Box
        sx={{
          p: 3,
          backgroundColor: "#f8f9fa",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Exibe erro se houver problema na busca
  if (error) {
    return (
      <Box sx={{ p: 3, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Erro ao carregar configurações de impostos: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header da página */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <AccountBalanceIcon
            sx={{ fontSize: "2rem", color: "#ff7043", mr: 2 }}
          />
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: "#1a1a1a",
              fontSize: { xs: "1.75rem", md: "2.125rem" },
            }}
          >
            Configuração de Impostos
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{ color: "#666", fontSize: "1rem", ml: 6 }}
        >
          Configure o regime tributário da sua empresa e as configurações
          específicas para cálculo de impostos
        </Typography>
      </Box>
      <TaxRegimeSelector
        selectedRegime={tempSelectedRegime}
        onRegimeChange={handleRegimeChange}
        onConfirmRegimeChange={handleTaxProfileUpdate}
        isUpdatingProfile={isProfileUpdating}
      />
      {renderConfigComponent()}
      <SpecialRegimeCard
        enabled={tempSpecialRegimeEnabled}
        onToggle={handleSpecialRegimeToggle}
        selectedRegime={tempSelectedRegime}
        onConfigChange={() => setPendingChanges(true)}
        initialData={{
          icms_nacional: taxData?.tax?.regimeEspecial?.icms_nacional || 0,
          icms_importado: taxData?.tax?.regimeEspecial?.icms_importado || 0,
        }}
      />{" "}
      {pendingChanges && (
        <Tooltip
          title={isUpdating ? "Salvando..." : "Salvar alterações"}
          placement="left"
        >
          <Fab
            color="primary"
            onClick={handleSaveChanges}
            disabled={isUpdating}
            sx={{
              position: "fixed",
              bottom: 32,
              right: 32,
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
              "&:hover": {
                boxShadow: "0 6px 15px rgba(0, 0, 0, 0.3)",
              },
              zIndex: 1000,
            }}
          >
            {isUpdating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <SaveIcon />
            )}
          </Fab>
        </Tooltip>
      )}
      <Snackbar
        open={alert}
        autoHideDuration={4000}
        onClose={() => setAlert(false)}
      >
        <Alert
          onClose={() => setAlert(false)}
          severity={severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
